/** The hub's notification and approval rows, on the shared pocket row (spec §4, §5). */
import type { NotificationRowData, PendingApproval } from "@/api/notifications";
import { Button, CONCEPT_ICONS, Icons, PocketRow } from "@/components/ui";
import { HighlightedTitle } from "@/components/panels/search-modal/highlighted-title";
import { useT } from "@/i18n";
import { panelsNotificationsMessages } from "@/i18n/messages/panels-notifications";
import { approvalWords, notificationRowWords } from "./notification-words";

/** A notification uses the icon of what it is about (icon vocabulary). */
function rowIcon(row: NotificationRowData) {
  if (row.kind === "task_comment") return CONCEPT_ICONS.chat;
  if (row.target?.type === "conversation") return CONCEPT_ICONS.chat;

  switch (row.kind) {
    case "task_completed":
    case "task_blocked":
    case "task_assigned":
    case "capacity_deferred":
      return CONCEPT_ICONS.task;

    case "memory_learned":
    case "memory_review":
      return CONCEPT_ICONS.memory;
    case "cli_error":
    case "execution_failed":
      return CONCEPT_ICONS.chat;
    default:
      return CONCEPT_ICONS.notification;
  }
}

export interface NotificationRowProps {
  row: NotificationRowData;
  query: string;
  selected?: boolean;
  onOpen: (row: NotificationRowData) => void;
  onClear: (row: NotificationRowData) => void;
}

export function NotificationRow({
  row,
  query,
  selected = false,
  onOpen,
  onClear,
}: NotificationRowProps) {
  const t = useT(panelsNotificationsMessages);
  const words = notificationRowWords(row);
  const Icon = rowIcon(row);
  const releaseNotes = row.target?.type === "release_notes";
  return (
    <PocketRow
      testId={`hub-row-notification-${row.id}`}
      leading={<Icon className="h-4 w-4" aria-hidden="true" />}
      title={<HighlightedTitle text={words.title} query={query} />}
      secondLine={releaseNotes ? undefined : words.secondLine}
      below={
        releaseNotes ? (
          <>
            <p
              className="w-full whitespace-pre-line text-appkit-small text-secondary"
              data-testid={`hub-release-notes-${row.id}`}
            >
              {row.description}
            </p>
            {!row.read ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onOpen(row)}
                data-testid={`hub-release-notes-read-${row.id}`}
              >
                {t("gotIt")}
              </Button>
            ) : null}
          </>
        ) : undefined
      }
      status={words.tone ? { tone: words.tone } : undefined}
      selected={selected}
      onSelect={row.target ? () => onOpen(row) : undefined}
      hoverActions={
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          aria-label={t("clear")}
          title={t("clear")}
          onClick={() => onClear(row)}
          data-testid={`hub-clear-${row.id}`}
        >
          <Icons.X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      }
    />
  );
}

export interface ApprovalRowProps {
  approval: PendingApproval;
  query: string;
  selected?: boolean;
  onOpen: (approval: PendingApproval) => void;
  onDecide: (approval: PendingApproval, allow: boolean) => void;
}

export function ApprovalRow({
  approval,
  query,
  selected = false,
  onOpen,
  onDecide,
}: ApprovalRowProps) {
  const t = useT(panelsNotificationsMessages);
  const words = approvalWords(approval);
  const Icon = CONCEPT_ICONS.approval;
  return (
    <PocketRow
      testId={`hub-row-approval-${approval.id}`}
      leading={<Icon className="h-4 w-4" aria-hidden="true" />}
      title={<HighlightedTitle text={words.title} query={query} />}
      secondLine={words.secondLine}
      status={{ tone: "question" }}
      selected={selected}
      onSelect={() => onOpen(approval)}
      below={
        <div className="mt-2 flex gap-1.5">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onDecide(approval, false)}
            data-testid={`hub-deny-${approval.id}`}
          >
            {t("deny")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onDecide(approval, true)}
            data-testid={`hub-allow-${approval.id}`}
          >
            {t("allow")}
          </Button>
        </div>
      }
    />
  );
}

/** “Mark all read” and “Clear all”, on the first section title after Needs you. */
export function NotificationListActions({
  onMarkAllRead,
  onClearAll,
}: {
  onMarkAllRead: () => void;
  onClearAll: () => void;
}) {
  const t = useT(panelsNotificationsMessages);
  return (
    <span className="flex items-center gap-3.5 normal-case tracking-normal">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-auto px-0"
        onClick={onMarkAllRead}
        data-testid="hub-mark-all-read"
      >
        {t("markAllRead")}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-auto px-0"
        onClick={onClearAll}
        data-testid="hub-clear-all"
      >
        {t("clearAll")}
      </Button>
    </span>
  );
}
